import React from 'react';
import PropTypes from 'prop-types';

import {
	IMAGE_WIDTHS,
	buildImageSrcSet,
	buildImageSizesForContentGrid,
	resizeImage,
} from '@techchange/filestack';
import { CustomLink } from '@techchange/components';
import { isSafeUrl } from '../../../../utils/urls';

const propTypes = {

	/**
	 * The unique ID for the content.
	 */
	id: PropTypes.string,

	/**
	 * The title to display.
	 */
	title: PropTypes.string,

	/**
	 * Flag indicating if the content title should be visible.
	 */
	showTitle: PropTypes.bool,

	/**
	 * Additional data for the content.
	 */
	data: PropTypes.shape({

		/**
		 * The image description text (if any).
		 */
		alt: PropTypes.string,

		/**
		 * The image link (if any).
		 */
		href: PropTypes.string,

		/**
		 * The image url.
		 */
		src: PropTypes.string,

		/**
		 * The image link target (if any).
		 */
		target: PropTypes.oneOf(['_blank', '_self']),

	}),

	/**
	 * The width of the content.
	 */
	width: PropTypes.number,

	/**
	 * The scope of the content,
	 * i.e. "session", "course", "organization", etc.
	 */
	scope: PropTypes.string.isRequired,

	/**
	 * The unique scope ID of the content.
	 */
	scopeId: PropTypes.string.isRequired,

	/**
	 * Callback function to handle clicking a link.
	 */
	onLinkClick: PropTypes.func,

};

const ContentDisplay = (props) => {

	const {
		id,
		title,
		showTitle,
		data,
		width,
		scope,
		scopeId,
		onLinkClick,
	} = props;

	if (!data) {
		return null;
	}

	const { src, href, target, alt } = data;

	const srcSet = buildImageSrcSet(src, IMAGE_WIDTHS);

	let img = (
		<img
			className="img-fluid w-100"
			srcSet={srcSet}
			sizes={srcSet && buildImageSizesForContentGrid(width)}
			src={resizeImage(src)}
			title={showTitle ? title : undefined}
			alt={alt} />
	);

	if (href && isSafeUrl(href, ['http:', 'https:', 'mailto:', 'tel:'])) {

		const linkTarget = target || '_blank';

		img = (
			<CustomLink
				className="d-flex"
				url={href}
				target={linkTarget}
				resourceId={id}
				scope={scope}
				scopeId={scopeId}
				onClick={onLinkClick}>
				{img}
			</CustomLink>
		);

	}

	return (
		<div>
			{img}
		</div>
	);

};

ContentDisplay.propTypes = propTypes;

export default ContentDisplay;
